import AttachFile from "@mui/icons-material/AttachFile";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, Link, Stack, SvgIconProps, Typography } from "@mui/material";
import { SubmissionStatus } from "generated-graphql/graphql";
import { ComponentType, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { SectionHeader } from "./SectionHeader";
import {
  type ComplianceLine,
  type ComplianceLinesInput,
  getComplianceLines,
  isSubjectToEManifestUpload,
  NOT_SUBJECT_LABEL,
} from "./summaryStatus";
import { ManifestDetail } from "./types";

/** Official eCFR text of the regulation the upload obligations come from. */
const ECFR_262_42_URL = "https://www.ecfr.gov/current/title-40/section-262.42";

type ComplianceManifest = Pick<
  ManifestDetail,
  | "isEManifest"
  | "daysSinceShipment"
  | "reportedWasteGeneratorStatus"
  | "exceptionReportSubmission"
  | "transporterContactedAt"
>;

interface ComplianceSectionProps {
  manifest: ManifestDetail;
  tenantId: string;
  showExceptionReporting: boolean;
  hasProofOfContact: boolean;
  creatingExceptionReport: boolean;
  onAddProofOfContact: () => void;
  onCreateExceptionReport: () => void;
  /** Preview the uploaded proof-of-contact document, when one exists. */
  onViewProofOfContact?: () => void;
}

function buildLinesInput(
  manifest: ComplianceManifest,
  hasProofOfContact: boolean
): ComplianceLinesInput {
  let exceptionReportStatus: "none" | "started" | "filed" = "none";
  if (manifest.exceptionReportSubmission?.status === SubmissionStatus.FilingComplete) {
    exceptionReportStatus = "filed";
  } else if (manifest.exceptionReportSubmission) {
    exceptionReportStatus = "started";
  }
  return {
    subjectToUpload: isSubjectToEManifestUpload(manifest.reportedWasteGeneratorStatus),
    notSubjectLabel: NOT_SUBJECT_LABEL[manifest.reportedWasteGeneratorStatus ?? ""],
    hasEManifest: !!manifest.isEManifest,
    daysSinceShipment: manifest.daysSinceShipment ?? 0,
    contacted: manifest.transporterContactedAt != null || hasProofOfContact,
    exceptionReportStatus,
  };
}

const EXCEPTION_KINDS = new Set<ComplianceLine["kind"]>([
  "exceptionRequired",
  "exceptionStarted",
  "exceptionFiled",
]);

/** The lines the section renders. The section is always shown, so this includes
 * the positive on-track / uploaded / not-subject states too; only exception
 * lines are gated, by their feature flag. */
function visibleComplianceLines(args: {
  manifest: ComplianceManifest;
  hasProofOfContact: boolean;
  showExceptionReporting: boolean;
}): ComplianceLine[] {
  return getComplianceLines(buildLinesInput(args.manifest, args.hasProofOfContact)).filter((l) => {
    if (!args.showExceptionReporting && EXCEPTION_KINDS.has(l.kind)) return false;
    return true;
  });
}

/**
 * One compliance line: an icon that sets the line off (like a bullet), a bold
 * title in the normal text color, and optional supporting text / actions. No
 * box — the lines sit directly in the section card.
 */
function ComplianceItem({
  Icon,
  color,
  title,
  bold = true,
  children,
}: {
  Icon: ComponentType<SvgIconProps>;
  color: string;
  title: string;
  /** Bold by default; positive/reassurance lines render at normal weight. */
  bold?: boolean;
  children?: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Icon sx={{ fontSize: "1.125rem", color, flexShrink: 0, mt: "1px" }} />
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={bold ? 600 : 400}>
          {title}
        </Typography>
        {children}
      </Stack>
    </Stack>
  );
}

/**
 * e-Manifest upload compliance: the 45-/60-day obligations, exception-report
 * state, and a transporter/TSDF-contacted note — each as an icon-led line,
 * driven by the same {@link getComplianceLines} model the Summary uses. Only
 * rendered when {@link hasComplianceContent} is true. Source-agnostic.
 */
export function ComplianceSection({
  manifest,
  tenantId,
  showExceptionReporting,
  hasProofOfContact,
  creatingExceptionReport,
  onAddProofOfContact,
  onCreateExceptionReport,
  onViewProofOfContact,
}: ComplianceSectionProps) {
  const lines = visibleComplianceLines({ manifest, hasProofOfContact, showExceptionReporting });

  const submission = manifest.exceptionReportSubmission;

  const renderLine = (line: ComplianceLine): ReactNode => {
    switch (line.kind) {
      case "contactNeeded":
        return (
          <ComplianceItem
            key={line.kind}
            Icon={ErrorIcon}
            color="warning.main"
            title={line.message}
          >
            <Typography variant="body2" color="text.secondary">
              <Link href={ECFR_262_42_URL} target="_blank" rel="noopener">
                40 CFR 262.42(a)
              </Link>
              : if no signed manifest within 45 days, contact the transporter/TSDF; if unresolved at
              60 days, file an EPA Exception Report.
            </Typography>
            <Button
              size="small"
              startIcon={<AttachFile />}
              onClick={onAddProofOfContact}
              sx={{ alignSelf: "flex-start" }}
            >
              Add Proof of Contact
            </Button>
          </ComplianceItem>
        );
      case "exceptionRequired":
        return (
          <ComplianceItem key={line.kind} Icon={ErrorIcon} color="error.main" title={line.message}>
            <Button
              size="small"
              color="error"
              disabled={creatingExceptionReport}
              onClick={onCreateExceptionReport}
              sx={{ alignSelf: "flex-start" }}
            >
              {creatingExceptionReport ? "Creating…" : "Create Exception Report"}
            </Button>
          </ComplianceItem>
        );
      case "exceptionStarted":
      case "exceptionFiled":
        return (
          <ComplianceItem
            key={line.kind}
            Icon={line.kind === "exceptionFiled" ? CheckCircle : InfoIcon}
            color={line.kind === "exceptionFiled" ? "success.main" : "info.main"}
            title={
              line.kind === "exceptionFiled" ? "Exception report filed" : "Exception report started"
            }
          >
            {submission && (
              <Typography variant="body2">
                <Link
                  component={RouterLink}
                  to={`/o/${tenantId}/waste/submissions/${submission.id}`}
                >
                  {submission.title}
                </Link>
              </Typography>
            )}
          </ComplianceItem>
        );
      case "contacted":
        return (
          <ComplianceItem
            key={line.kind}
            Icon={CheckCircle}
            color="success.main"
            title="Transporter/TSDF contacted"
          >
            {onViewProofOfContact && (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onViewProofOfContact}
                sx={{ alignSelf: "flex-start" }}
              >
                Proof of Contact
              </Link>
            )}
          </ComplianceItem>
        );
      case "onTrack":
      case "uploaded":
      case "notSubject":
        // Positive states — shown now that the section is always visible. Normal
        // weight: these are reassurance, not a call to action.
        return (
          <ComplianceItem
            key={line.kind}
            Icon={CheckCircle}
            color="success.main"
            title={line.message}
            bold={false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <SectionHeader title="e-Manifest Upload Compliance" />
      <Stack spacing={1.5}>{lines.map(renderLine)}</Stack>
    </Box>
  );
}
